/**
 * OverflowMenu: the shared "⋯" menu for a header's or toolbar's rare, admin and destructive actions, so that the
 * row keeps one primary button and a few quiet ones.
 *
 *   import OverflowMenu from "../../components/ui/OverflowMenu"; // the path from your file
 *
 *   <OverflowMenu
 *     label={t("folders.more_actions")}  // required: the "⋯" button's aria-label and tooltip (say whose actions)
 *     items={[
 *       { key: "settings", label: t("…"), Icon: Cog6ToothIcon, onClick: () => openSettings() },
 *       { key: "open", label: t("…"), Icon: EyeIcon, to: `/questions?folder=${uuid}` }, // a react-router Link
 *       { key: "archive", label: t("…"), Icon: ArchiveBoxIcon, onClick: archive, disabled: !canArchive },
 *       isAdmin && { key: "delete", label: t("…"), Icon: TrashIcon, danger: true, onClick: () => askToDelete() },
 *     ]}
 *     align="right"        // "right" (default): the menu's right edge under the button's; "left": its left edge
 *     portal={false}       // true inside a table, a sideways-scrolling row or an overflow-hidden card (see below)
 *     testId="bts-actions" // optional: the button's data-testid; each item gets "<testId>-<key>"
 *     className=""         // added to the wrapper, e.g. "max-md:order-last"
 *   />
 *
 * - items: { key, label, Icon?, onClick?, to?, danger?, disabled? }. Falsy entries are skipped, so `cond && {…}`
 *   works; with no item left the menu renders nothing (no empty "⋯").
 * - danger: red text, after a thin line that sets the dangerous items apart; the action must still ask for
 *   confirmation in its own dialog (the menu does not).
 * - disabled: greyed out, not clickable, skipped by the arrow keys.
 * - The menu closes on a choice and gives the focus back to "⋯". Keyboard: Enter/Space/arrows open, arrows move,
 *   Esc closes (Headless UI Menu). "⋯" stays grey while its menu is open.
 * - Where it opens: under the button, or above it when the screen has no room below. By default the menu lives
 *   inside the page (absolute), so a parent with overflow hidden or auto (a table's scroll box, a sideways-scrolling
 *   row, an overflow-hidden card) would clip it: there pass `portal`, and the menu is drawn over the page (fixed,
 *   following its button while anything scrolls). Inside a dialog `portal` is ignored (a dialog keeps the focus and
 *   the clicks inside itself), so a menu in a dialog must not sit in a clipping box.
 * Look: a framed 44px square on phones (it sits next to a full-width primary button), a plain 36px icon button from
 * md up; a white card of 16rem (never wider than the screen) whose items are 44px tall on phones.
 */
import React, { Fragment, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { Menu, Transition } from "@headlessui/react";
import { EllipsisHorizontalIcon } from "@heroicons/react/24/outline";

const focusRing = "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600";

// The gap between the button and the menu (mt-1), the room kept free above and below it, and the side margin of a
// menu drawn over the page.
const GAP = 4;
const EDGE = 8;
const SIDE = 16;

const buttonClass = ({ open }) =>
  `inline-flex h-11 w-11 items-center justify-center rounded-lg shadow-sm ring-1 ring-inset ring-slate-300 transition duration-200 hover:bg-slate-100 hover:text-slate-900 md:h-9 md:w-9 md:shadow-none md:ring-0 ${
    open ? "bg-slate-100 text-slate-900" : "bg-white text-slate-600 md:bg-transparent"
  } ${focusRing}`;

const itemClass = (active, danger, disabled) =>
  `flex min-h-[2.75rem] w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm md:min-h-0 ${
    disabled
      ? `cursor-not-allowed ${danger ? "text-red-300" : "text-slate-400"}`
      : danger
        ? `text-red-600 ${active ? "bg-red-50" : ""}`
        : active
          ? "bg-slate-100 text-slate-900"
          : "text-slate-700"
  }`;

// The room the menu has: the window's height, or for a menu inside the page its nearest scrolling ancestor's (the
// app scrolls inside <main>, under its header).
function roomOf(button, floating) {
  let top = 0;
  let bottom = window.innerHeight;
  if (!floating) {
    for (let node = button.parentElement; node && node !== document.body; node = node.parentElement) {
      const { overflowY } = window.getComputedStyle(node);
      if (overflowY === "auto" || overflowY === "scroll" || overflowY === "hidden") {
        const box = node.getBoundingClientRect();
        top = Math.max(top, box.top);
        bottom = Math.min(bottom, box.bottom);
        break;
      }
    }
  }
  return { top, bottom };
}

// The menu card. Measured when it appears: above the button where there is no room below (and no taller than the
// room it has, scrolling inside); with `floating` it is drawn in document.body at the button's place (fixed) and
// follows it while anything scrolls.
function MenuCard({ open, buttonRef, align, floating, list, testId }) {
  // The card's DOM node while it is on screen (a callback ref: it appears a render after `open`).
  const [card, setCard] = useState(null);
  const [place, setPlace] = useState({ up: false, at: null, maxHeight: null });

  useLayoutEffect(() => {
    if (!card || !open) return undefined;
    const update = () => {
      const button = buttonRef.current;
      if (!button) return;
      const box = button.getBoundingClientRect();
      const room = roomOf(button, floating);
      // The card's own size: its whole content (scrollHeight, even while it is cut to the room), without the
      // opening animation's scale.
      const width = card.offsetWidth;
      const height = card.scrollHeight;
      const below = room.bottom - box.bottom - GAP - EDGE;
      const above = box.top - room.top - GAP - EDGE;
      const up = height > below && above > below;
      const space = Math.max(120, Math.round(up ? above : below));
      const maxHeight = height > space ? space : null;
      let at = null;
      if (floating) {
        const screen = document.documentElement.clientWidth;
        const shown = Math.min(height, space);
        at = {
          left: Math.round(Math.max(SIDE, Math.min(align === "left" ? box.left : box.right - width, screen - SIDE - width))),
          top: Math.round(up ? box.top - GAP - shown : box.bottom + GAP),
        };
      }
      setPlace((now) =>
        now.up === up && now.maxHeight === maxHeight && (now.at === at || (now.at && at && now.at.left === at.left && now.at.top === at.top))
          ? now
          : { up, at, maxHeight }
      );
    };
    update();
    if (!floating) return undefined;
    // Capture: the app scrolls inside <main>, not the document.
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [card, open, floating, align, buttonRef]);

  const firstDanger = list.findIndex((item) => item.danger);
  const origin = place.up
    ? align === "left"
      ? "origin-bottom-left"
      : "origin-bottom-right"
    : align === "left"
      ? "origin-top-left"
      : "origin-top-right";
  const position = floating
    ? "fixed z-40"
    : `absolute z-20 ${align === "left" ? "left-0" : "right-0"} ${place.up ? "bottom-full mb-1" : "mt-1"}`;
  const menu = (
    <Transition
      as={Fragment}
      enter="transition ease-out duration-100 motion-reduce:transition-none"
      enterFrom="opacity-0 scale-95"
      enterTo="opacity-100 scale-100"
      leave="transition ease-in duration-75 motion-reduce:transition-none"
      leaveFrom="opacity-100 scale-100"
      leaveTo="opacity-0 scale-95"
    >
      <Menu.Items
        ref={setCard}
        // Drawn over the page, the card is transparent until it is measured (a render before the paint): not
        // hidden, as Headless UI moves the focus into it at once.
        style={{
          maxHeight: place.maxHeight ?? undefined,
          ...(floating ? { left: place.at?.left ?? 0, top: place.at?.top ?? 0, opacity: place.at ? undefined : 0 } : null),
        }}
        className={`${position} ${origin} w-64 max-w-[calc(100vw-2rem)] overflow-y-auto rounded-xl bg-white p-1 shadow-lg ring-1 ring-slate-900/5 focus:outline-none`}
      >
        {list.map(({ key, label: itemLabel, Icon, onClick, to, danger = false, disabled = false }, index) => (
          <Fragment key={key}>
            {index === firstDanger && index > 0 && <div role="separator" className="mx-2 my-1 h-px bg-slate-100" />}
            <Menu.Item disabled={disabled}>
              {({ active }) => {
                const content = (
                  <>
                    {Icon && <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />}
                    <span className="min-w-0 flex-1">{itemLabel}</span>
                  </>
                );
                const itemTestId = testId ? `${testId}-${key}` : undefined;
                return to && !disabled ? (
                  <Link to={to} onClick={onClick} data-testid={itemTestId} className={itemClass(active, danger, false)}>
                    {content}
                  </Link>
                ) : (
                  <button
                    type="button"
                    onClick={disabled ? undefined : onClick}
                    disabled={disabled}
                    data-testid={itemTestId}
                    className={itemClass(active, danger, disabled)}
                  >
                    {content}
                  </button>
                );
              }}
            </Menu.Item>
          </Fragment>
        ))}
      </Menu.Items>
    </Transition>
  );
  return floating ? createPortal(menu, document.body) : menu;
}

export default function OverflowMenu({ items, label, align = "right", portal = false, testId, className = "" }) {
  const buttonRef = useRef(null);
  const list = (items || []).filter(Boolean);
  const hasItems = list.length > 0;
  // A dialog keeps the focus and the clicks inside itself: there the menu stays in place.
  const [inDialog, setInDialog] = useState(false);
  useLayoutEffect(() => {
    if (hasItems && portal) setInDialog(!!buttonRef.current?.closest("[role='dialog']"));
  }, [hasItems, portal]);
  if (!hasItems) return null;
  return (
    <Menu as="div" className={`relative ${className}`}>
      {({ open }) => (
        <>
          <Menu.Button ref={buttonRef} aria-label={label} title={label} data-testid={testId} className={buttonClass}>
            <EllipsisHorizontalIcon className="h-5 w-5" aria-hidden="true" />
          </Menu.Button>
          <MenuCard open={open} buttonRef={buttonRef} align={align} floating={portal && !inDialog} list={list} testId={testId} />
        </>
      )}
    </Menu>
  );
}
